/**
 * PriceToggle - Global price mode manager
 * ==================================================
 * 
 * Handles switching between monthly (leasing) and one-time (purchase) price display
 * across the entire storefront. Persists user preference in localStorage.
 * 
 * Architecture:
 * - Sets body[data-price-mode="monthly|onetime"] attribute
 * - CSS rules in price-toggle.css handle visibility based on this attribute
 * - Dispatches 'price-mode-changed' custom event for components needing JS updates
 * - Subscribes to variant changes to update prices when product variants change
 * 
 * Integration points:
 * - price-toggle-component.liquid: The toggle switch UI
 * - price-with-toggle.liquid: Product price display (products, cards)
 * - cart-price-toggle.liquid: Cart line item and total price display
 * 
 * Theme Settings (from window.themeSettings):
 * - defaultPriceMode: 'monthly' | 'onetime' - Initial mode for new visitors
 * - leasingMonths: Number - Months for monthly price calculation (default: 36)
 * 
 * @example
 * // Get current mode
 * const mode = PriceToggle.getMode(); // 'monthly' or 'onetime'
 * 
 * // Listen for mode changes
 * document.addEventListener('price-mode-changed', (e) => {
 *   console.log('New mode:', e.detail.mode);
 * });
 */
class PriceToggle {
  constructor() {
    this.storageKey = 'jobbsykkel-price-mode';
    
    // Read defaults from theme settings (injected in theme.liquid)
    const themeSettings = window.themeSettings || {};
    this.defaultMode = themeSettings.defaultPriceMode || 'monthly';
    this.months = themeSettings.leasingMonths || 36;
    
    this.init();
  }

  /**
   * Initialize the price toggle system
   */
  init() {
    // Get stored preference or use default from theme settings
    this.mode = localStorage.getItem(this.storageKey) || this.defaultMode;
    
    // Set initial state on body
    document.body.setAttribute('data-price-mode', this.mode);
    
    // Bind all toggle switches
    this.bindToggles();
    
    // Update label styling
    this.updateLabels();
    
    // Subscribe to variant changes for dynamic price updates
    this.subscribeToVariantChanges();

    // Sync initial mode to cart attributes for checkout extension
    this.syncPriceModeToCart();
  }

  /**
   * Bind click handlers to all toggle switches
   */
  bindToggles() {
    document.querySelectorAll('.price-toggle-switch').forEach(toggle => {
      // Set initial state
      const isMonthly = this.mode === 'monthly';
      toggle.setAttribute('aria-checked', isMonthly.toString());
      toggle.closest('.price-toggle')?.setAttribute('data-mode', this.mode);

      // Remove existing event listeners by cloning the element
      const newToggle = toggle.cloneNode(true);
      toggle.parentNode.replaceChild(newToggle, toggle);
      
      newToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });
  }

  /**
   * Toggle between monthly and onetime modes
   */
  toggle() {
    this.mode = this.mode === 'monthly' ? 'onetime' : 'monthly';
    localStorage.setItem(this.storageKey, this.mode);
    
    // Update body attribute
    document.body.setAttribute('data-price-mode', this.mode);
    
    // Update all toggle switches
    document.querySelectorAll('.price-toggle-switch').forEach(toggle => {
      const isMonthly = this.mode === 'monthly';
      toggle.setAttribute('aria-checked', isMonthly.toString());
      toggle.closest('.price-toggle')?.setAttribute('data-mode', this.mode);
    });

    // Update labels
    this.updateLabels();
    
    // Dispatch custom event for other components
    document.dispatchEvent(new CustomEvent('price-mode-changed', { 
      detail: { mode: this.mode, months: this.months } 
    }));

    // Sync mode to cart attributes for checkout extension
    this.syncPriceModeToCart();
  }

  /**
   * Sync price mode to Shopify cart attributes for checkout extension
   * Only sends leasing_months when mode is 'monthly'
   */
  async syncPriceModeToCart() {
    try {
      const attributes = {
        priceMode: this.mode
      };
      
      // Only include leasing months when in monthly mode
      if (this.mode === 'monthly') {
        attributes.leasingMonths = String(this.months);
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
    } catch (error) {
      console.warn('PriceToggle: Could not sync mode to cart', error);
    }
  }

  /**
   * Update toggle label active states
   */
  updateLabels() {
    document.querySelectorAll('.price-toggle').forEach(container => {
      const monthlyLabel = container.querySelector('[data-label="monthly"]');
      const onetimeLabel = container.querySelector('[data-label="onetime"]');
      
      if (monthlyLabel && onetimeLabel) {
        if (this.mode === 'monthly') {
          monthlyLabel.classList.add('is-active');
          onetimeLabel.classList.remove('is-active');
        } else {
          onetimeLabel.classList.add('is-active');
          monthlyLabel.classList.remove('is-active');
        }
      }
    });
  }

  /**
   * Subscribe to variant changes to update price displays
   * Uses the pub/sub system from pubsub.js
   */
  subscribeToVariantChanges() {
    // Check if pub/sub is available (loaded from pubsub.js)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const { data } = event;
        if (data && data.variant) {
          this.updatePriceContainers(data.sectionId, data.variant.price);
        }
      });
    }
  }

  /**
   * Update price containers when variant changes
   * @param {string} sectionId - The section ID containing the price
   * @param {number} priceInCents - The new price in cents
   */
  updatePriceContainers(sectionId, priceInCents) {
    // Find price containers within the section
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;

    const priceContainers = section.querySelectorAll('[data-price-container]');
    priceContainers.forEach(container => {
      this.updatePriceDisplay(container, priceInCents);
    });
  }

  /**
   * Update a single price container with new price values
   * @param {HTMLElement} container - The price container element
   * @param {number} priceInCents - The new price in cents
   */
  updatePriceDisplay(container, priceInCents) {
    const months = parseInt(container.dataset.months) || this.months;
    const monthlyPrice = Math.round(priceInCents / months / 100) * 100;
    
    // Update data attribute
    container.dataset.priceCents = priceInCents;
    
    // Update displayed amounts (querySelectorAll supports multiple elements per container)
    container.querySelectorAll('[data-monthly-amount]').forEach(el => {
      el.textContent = this.formatMoney(monthlyPrice);
    });
    container.querySelectorAll('[data-onetime-amount]').forEach(el => {
      el.textContent = this.formatMoney(priceInCents);
    });
  }

  /**
   * Format cents to money string (simple Norwegian format)
   * Uses Shopify's money format if available
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  formatMoney(cents) {
    // Try to use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Fallback: Simple Norwegian format (kr X XXX)
    const amount = (cents / 100).toFixed(0);
    const formatted = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `kr ${formatted}`;
  }

  /**
   * Get current price mode
   * @returns {'monthly' | 'onetime'} Current mode
   */
  static getMode() {
    return localStorage.getItem('jobbsykkel-price-mode') || 
           (window.themeSettings?.defaultPriceMode) || 
           'monthly';
  }

  /**
   * Get configured leasing months
   * @returns {number} Number of months
   */
  static getMonths() {
    return window.themeSettings?.leasingMonths || 36;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.priceToggle = new PriceToggle();
  });
} else {
  window.priceToggle = new PriceToggle();
}

// Re-initialiser ved Shopify section refresh
document.addEventListener('shopify:section:load', () => {
  window.priceToggle = new PriceToggle();
});